import createCrudService from '@core/api/create-crud-service';
import { QUERY_KEYS } from '@shared/constants/query-keys';
import { queryClient } from '@src/core/api/query-client';
import { request } from '@src/core/api/request';
import type { TBaseQuery } from '@src/shared/types/base-query';
import type { TLeadNote } from '@src/shared/types/lead-note';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import { useMutation, useQuery } from '@tanstack/react-query';

export type TLeadNoteQuery = TBaseQuery & {
  lead: number;
  o: (
    | 'id'
    | '-id'
    | 'created_at'
    | '-created_at'
    | 'order'
    | '-order'
    | 'next_follow_up_date'
    | '-next_follow_up_date'
  )[];
  has_next_follow_up_date: boolean;
  is_resolved?: boolean;
  status?: TLeadNote['status'][];
  channel?: TLeadNote['channel'][];
  assignee?: string[];
  screening_meeting_date_after?: string;
  screening_meeting_date_before?: string;
};

export type TLeadNotesStats = {
  today: number;
  tomorrow: number;
  this_week: number;
  this_month: number;
  overdue?: number;
};

type TLeadNotePayload = {
  attachments?: number[];
  lead: number;
  note: string;
  next_follow_up_date?: string;
  channel?: TLeadNote['channel'];
  status?: TLeadNote['status'];
  is_resolved?: boolean;
};

const leadNoteApi = {
  ...createCrudService<TLeadNote, TLeadNoteQuery, TLeadNotePayload>({
    queryKey: [QUERY_KEYS.getLeadNotes],
    url: 'lead-notes',
    validateQuery: [QUERY_KEYS.getLeads],
  }),
  useGetLeadNotesStats: () =>
    useQuery({
      queryKey: [QUERY_KEYS.getLeadNotesStats],
      queryFn: async () => {
        const response: TLeadNotesStats = await request({
          url: 'lead-notes/stats/',
          method: 'GET',
        });
        return response;
      },
    }),
  useResolveLeadNote: () =>
    useMutation({
      mutationFn: async (id: number) => {
        await request({
          url: `lead-notes/${id}/resolve/`,
          method: 'PATCH',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getLeadNotes] });
        showToast({
          title: 'Note resolved successfully',
          description: 'The note has been resolved successfully',
          variant: 'success',
        });
      },
    }),

  useUnresolveLeadNote: () =>
    useMutation({
      mutationFn: async (id: number) => {
        await request({
          url: `lead-notes/${id}/unresolve/`,
          method: 'PATCH',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getLeadNotes] });
        showToast({
          title: 'Note unresolved successfully',
          description: 'The note has been unresolved successfully',
          variant: 'success',
        });
      },
    }),
};

export default leadNoteApi;
